import React, { useState, useRef, useEffect } from 'react';
import { CloudOutlined } from '@ant-design/icons';
import { decodeWorkWiseToken, getUserInfoFromToken } from '../../utils/helper';
import logoText from '../../assets/DasboardIcons/logo-text.png';
import './HeaderAvatar.css';
import { useDocument } from '../../context/documentInfoContex';
import { workwiseServices } from '../../api';
import { useCanvas } from '../../context/canvasContext';

const HeaderAvatar = ({ scale }) => {
  const { isAuthenticated } = useDocument();
  const userInfo = getUserInfoFromToken() || {};
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [storageInfo, setStorageInfo] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);
  const [storageError, setStorageError] = useState(null);
  const [effectiveScale, setEffectiveScale] = useState(scale || 1);

  useEffect(() => {
    if (scale != null) {
      setEffectiveScale(scale);
      return;
    }

    const dashboardContainer = document.querySelector('.dashboard-container');
    const zoom = dashboardContainer
      ? window.getComputedStyle(dashboardContainer).zoom
      : '1';

    // If dashboard is zoomed (e.g., 0.8), do not apply additional scaling.
    // Otherwise, shrink to match dashboard icon sizing.
    setEffectiveScale(zoom && zoom !== '1' ? 1 : 0.8);
  }, [scale]);

  useEffect(() => {
    function handleClick(e) {
      if (open && ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // fetch user storage balance when dropdown opens (only if authenticated)
  useEffect(() => {
    if (!open || !isAuthenticated) return;

    async function fetchStorage() {
      setStorageLoading(true);
      setStorageError(null);
      try {
        const response = await workwiseServices.getUserBalance();
        // expecting response.data.data.storage and response.data.data.allocatedStorage
        setStorageInfo(response.data?.data || null);
      } catch (err) {
        setStorageError(err.message || 'Failed to load storage info');
        setStorageInfo(null);
      } finally {
        setStorageLoading(false);
      }
    }

    fetchStorage();
  }, [open, isAuthenticated]);

  // helper to format bytes into human-readable sizes (GB/MB)
  const formatBytes = (bytes) => {
    if (bytes == null || isNaN(bytes)) return '0 B';
    const abs = Math.abs(Number(bytes));
    if (abs === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(abs) / Math.log(1024));
    const value = abs / Math.pow(1024, i);
    return `${value.toFixed(i >= 3 ? 2 : 1)} ${units[i]}`; // more precision for GB+
  };

  const redirectTo = (url) => {
    window.open(url, '_blank');
  };

  const getInitial = () => {
    if (!isAuthenticated || !userInfo?.name) return '?';
    return userInfo.name.charAt(0).toUpperCase();
  };

  function openLoginWindow() {
    const width = 900; // Desired width in pixels
    const height = 600; // Desired height in pixels
    const url = "https://workw.com/login"; // Or any other URL you want to load
    const windowName = "Workwise Authentication";
    const left = (window.screen.width / 2) - (width / 2);
    const top = 50;
    // Define window features
    const features = `width=${width},height=${height},left=${left},top=${top},
                    toolbar=no,menubar=no,scrollbars=no,resizable=yes,
                    location=no,directories=no,status=no`;

    window.open(url, windowName, features);
  }

  const firstName = userInfo?.name?.split(' ')[0] || '';
  const initial = getInitial();

  const baseSize = 40;
  const size = baseSize / effectiveScale;
  const wrapperStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size,
    height: size,
    transform: `scale(${effectiveScale})`,
    transformOrigin: 'center',
    position: 'relative',
    zIndex: 10000,
  };

  return (
    <div className="ha-root" ref={ref} style={wrapperStyle}>
      <button
        className="ha-btn"
        onClick={() => setOpen(!open)}
        aria-label="User menu"
      >
        {isAuthenticated && userInfo?.image ? (
          <img src={userInfo.image} alt={userInfo.name || 'User'} className="ha-avatar-img" />
        ) : (
          <div className="ha-avatar-initial" style={{ backgroundColor: 'var(--primary-blue)' }}>{initial}</div>
        )}
      </button>
      {open && (
        <div className="ha-dropdown" role="dialog">
          <div className="ha-dropdown-inner">
            {isAuthenticated ? (
              <>
                {/* Email header with close button */}
                <div className="ha-header">
                  <span className="ha-header-email">{userInfo?.email}</span>
                  <button className="ha-close" onClick={() => setOpen(false)} aria-label="Close">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>

                {/* Avatar */}
                <div className="ha-avatar-container">
                  {userInfo?.image ? (
                    <img src={userInfo.image} alt={userInfo.name || 'User'} className="ha-dropdown-img" />
                  ) : (
                    <div className="ha-dropdown-initial" style={{ backgroundColor: 'var(--primary-blue)' }}>{initial}</div>
                  )}
                </div>

                {/* Name greeting */}
                <div className="ha-name">Hi, {firstName}!</div>

                {/* Manage Workwise Account button */}
                <button className="ha-manage" onClick={() => redirectTo(`https://workw.com/user/${userInfo?.userId}`)}>Manage your Workwise Account</button>

                {/* Add account and Sign out buttons */}
                <div className="ha-actions">
                  <button className="ha-action-btn" onClick={() => redirectTo('https://workw.com/')}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Switch account</span>
                  </button>
                  <div className="ha-actions-divider"></div>
                  <button className="ha-action-btn" onClick={() => redirectTo('https://workw.com/')}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span>Sign out</span>
                  </button>
                </div>

                {/* Storage usage */}
                <div className="ha-storage">
                  <CloudOutlined style={{ fontSize: '20px', color: '#f9ab00' }} />
                  {storageLoading ? (
                    <span>Loading...</span>
                  ) : storageError ? (
                    <span>Storage info unavailable</span>
                  ) : storageInfo ? (
                    (() => {
                      const used = Number(storageInfo.storage) || 0;
                      const allocated = Number(storageInfo.allocatedStorage) || 0;
                      const percent = allocated > 0 ? Math.round((used / allocated) * 100) : 0;
                      return (
                        <span>{percent}% of {formatBytes(allocated)} used</span>
                      );
                    })()
                  ) : (
                    <span>—</span>
                  )}
                </div>

                {/* Footer links */}
                <div className="ha-footer">
                  <a href="https://workw.com" target="_blank" rel="noopener noreferrer" className="ha-footer-link">Privacy Policy</a>
                  <span className="ha-footer-dot">•</span>
                  <a href="https://workw.com" target="_blank" rel="noopener noreferrer" className="ha-footer-link">Terms of Service</a>
                </div>
              </>
            ) : (
              <div className="ha-login-prompt">
                <img
                  src={logoText}
                  alt="Workwise"
                />
                <span>Please log in to access your account</span>
                <button className="ha-login-btn" onClick={openLoginWindow}>Workwise Login</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderAvatar;
